/**
 * Service Worker registration with auto-update handling.
 *
 *  - Only registers in production (CRA dev server intercepts fetches, which
 *    would break hot reload if we let the SW take over).
 *  - When a new SW version *replaces an existing one* we auto-reload once
 *    so the customer picks up new assets without a manual refresh.
 *  - On the very first visit (no prior controller), we DO NOT reload —
 *    that was causing the "loads twice / flash once" experience for anyone
 *    opening a shared link in a fresh browser or PWA install, on every
 *    device (iOS Safari, Android Chrome, desktop).
 */
export function registerServiceWorker() {
  if (process.env.NODE_ENV !== "production") return;
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

  // Snapshot BEFORE registering: was this page already controlled by a SW?
  // If not (first visit / fresh install), we must not reload when the new
  // SW takes control — that's a normal first-install `controllerchange`
  // event, not an update. Reloading here caused the double-load flash.
  const hadPriorController = !!navigator.serviceWorker.controller;

  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadPriorController) return; // first install — nothing to refresh for
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        // Check for updates periodically while the app is open
        setInterval(() => reg.update().catch(() => {}), 60 * 60 * 1000);

        // Detect a new SW being installed
        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // A new version is waiting — activate it immediately. Our
              // controllerchange handler above will then reload (only when
              // there was a prior controller, i.e. a genuine update).
              newWorker.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
      })
      .catch(() => {
        /* swallow — PWA install isn't critical for shopping */
      });
  });
}
